import Service from '@ember/service';

import type { JsonObject } from 'type-fest';

/**
 * Standard event names supported by the OpenAI Ads Measurement Pixel that we
 * currently report. Add more from the supported list as new conversions are
 * tracked.
 *
 * @see https://developers.openai.com/ads/supported-events
 */
export type OpenAiPixelEventName = 'lead_created' | 'trial_started';

export interface OpenAiPixelEventOptions {
  /** Reuse the same `event_id` across browser and server events to deduplicate them. */
  event_id?: string;
}

/**
 * Thin wrapper around the OpenAI Ads Measurement Pixel (`oaiq`).
 *
 * The `oaiq` SDK is injected by the backend host page (alongside the Segment
 * snippet), so this service only sends events to the global. All calls no-op
 * gracefully when the pixel is not present (e.g. in tests, or for ghost
 * sessions where the snippet is not rendered).
 *
 * @see https://developers.openai.com/ads/measurement-pixel
 */
export default class OpenAiPixelService extends Service {
  /**
   * Sends a conversion event to the OpenAI pixel.
   *
   * @param eventName    One of the supported standard event names, or `custom`.
   * @param eventProps   Event data describing the conversion (include `type` for every event).
   * @param eventOptions Extra delivery options (e.g. `event_id`, `custom_event_name`).
   */
  measure(eventName: OpenAiPixelEventName, eventProps?: JsonObject, eventOptions?: OpenAiPixelEventOptions): void {
    if (typeof window.oaiq === 'undefined') {
      return;
    }

    window.oaiq('measure', eventName, eventProps, eventOptions);
  }

  /**
   * Tracks a `lead_created` conversion (uses the `customer_action` data shape).
   */
  trackLeadCreated(eventOptions?: OpenAiPixelEventOptions): void {
    this.measure('lead_created', { type: 'customer_action' }, eventOptions);
  }

  /**
   * Tracks a `trial_started` conversion (uses the `plan_enrollment` data shape).
   *
   * @param planId Identifier for the plan the trial is for.
   */
  trackTrialStarted(planId: string, eventOptions?: OpenAiPixelEventOptions): void {
    this.measure('trial_started', { type: 'plan_enrollment', plan_id: planId }, eventOptions);
  }
}

declare module '@ember/service' {
  interface Registry {
    'openai-pixel': OpenAiPixelService;
  }
}
